import React, { useState } from 'react';
import { DollarSign, FileText, Link2, Briefcase, HelpCircle, X } from 'lucide-react';
import { FileUpload } from './FileUpload';
import { ProposalQualityScore } from './ProposalQualityScore';

export function ProposalForm({
  job,
  coverLetter,
  coverLetterSource,
  onCoverLetterSourceChange,
  proposalData,
  onProposalInputChange,
  onFileUpload,
  onRemoveAttachment,
  portfolio,
  loadingPortfolio,
  onPortfolioSelection,
  onSubmit,
  onCancel,
  isCreatingProposal,
  applyOnUpworkMode,
  error
}) {
  const [qualityCollapsed, setQualityCollapsed] = useState(false);
  const pricingAmount = Number(proposalData.pricing?.amount) || 0;
  const pricingType = proposalData.pricing?.type || 'fixed';
  const isHourly = pricingType === 'hourly';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Create Proposal</h3>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 rounded-full p-1 hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-5 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={onSubmit} className="space-y-5">
            {/* Job Information Card */}
            <div className="border border-gray-200 rounded-lg p-5">
              <h4 className="font-semibold text-gray-900 mb-3">Job details</h4>
              <h5 className="text-sm font-medium text-gray-900">{job.title}</h5>
              <p className="text-sm text-gray-600 mt-1 line-clamp-3">{job.description}</p>
              {job.skills && job.skills.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {job.skills.slice(0, 8).map((skill, i) => (
                    <span key={i} className="px-2.5 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs">
                      {typeof skill === 'string' ? skill : skill.name || ''}
                    </span>
                  ))}
                  {job.skills.length > 8 && (
                    <span className="text-xs text-gray-500 self-center">+{job.skills.length - 8} more</span>
                  )}
                </div>
              )}
            </div>

            {/* Cover Letter Card */}
            <div className="border border-gray-200 rounded-lg p-5">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <FileText className="h-4 w-4 text-gray-500" />
                  Cover letter
                </h4>
                <div className="flex rounded-md border border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => onCoverLetterSourceChange('ai')}
                    className={`px-3 py-1 text-xs font-medium transition-colors ${
                      coverLetterSource === 'ai'
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    AI Generated
                  </button>
                  <button
                    type="button"
                    onClick={() => onCoverLetterSourceChange('custom')}
                    className={`px-3 py-1 text-xs font-medium border-l border-gray-200 transition-colors ${
                      coverLetterSource === 'custom'
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    Custom
                  </button>
                </div>
              </div>
              <textarea
                value={proposalData.coverLetter}
                onChange={(e) => onProposalInputChange('coverLetter', e.target.value)}
                rows={6}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 resize-y"
                required
                placeholder={coverLetterSource === 'ai' ? 'AI-generated cover letter will appear here...' : 'Write your custom cover letter...'}
              />
              {coverLetterSource === 'ai' && coverLetter && (
                <p className="mt-1.5 text-xs text-gray-500">
                  Generated using AI based on the job requirements and your profile
                </p>
              )}
            </div>

            {/* Quality Score */}
            <ProposalQualityScore
              coverLetter={proposalData.coverLetter}
              job={job}
              collapsed={qualityCollapsed}
              onToggle={() => setQualityCollapsed(prev => !prev)}
            />

            {/* Your Proposed Terms Card */}
            <div className="border border-gray-200 rounded-lg p-5">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-gray-500" />
                Your proposed terms
              </h4>
              <div className="flex gap-3 items-end">
                <div className="flex-shrink-0">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                  <select
                    value={pricingType}
                    onChange={(e) => onProposalInputChange('pricing', { ...proposalData.pricing, type: e.target.value })}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="fixed">Fixed Price</option>
                    <option value="hourly">Hourly Rate</option>
                  </select>
                </div>
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    {isHourly ? 'Hourly rate' : 'Bid'}
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <input
                      type="number"
                      value={proposalData.pricing?.amount || ''}
                      onChange={(e) => onProposalInputChange('pricing', { ...proposalData.pricing, amount: parseInt(e.target.value) || 0 })}
                      className="w-full pl-7 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Currency</label>
                  <select
                    value={proposalData.pricing?.currency || 'USD'}
                    onChange={(e) => onProposalInputChange('pricing', { ...proposalData.pricing, currency: e.target.value })}
                    className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                  </select>
                </div>
              </div>

              {/* Fee Breakdown */}
              {pricingAmount > 0 && (
                <div className="mt-4 space-y-3 text-sm">
                  <div className="flex justify-between">
                    <div>
                      <div className="text-gray-500">{isHourly ? 'Hourly rate' : 'Fixed price'}</div>
                      <div className="text-gray-400 text-xs">Total amount the client will see</div>
                    </div>
                    <div className="text-gray-900 font-medium">
                      ${pricingAmount.toLocaleString()}{isHourly ? '/hr' : ''}
                    </div>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-gray-100">
                    <div>
                      <div className="text-gray-500">Upwork service fee (10%)</div>
                    </div>
                    <div className="text-gray-600">
                      -${(pricingAmount * 0.1).toFixed(2)}{isHourly ? '/hr' : ''}
                    </div>
                  </div>
                  <div className="flex justify-between pt-3 border-t border-gray-100">
                    <div>
                      <div className="text-gray-900 font-medium">You'll receive</div>
                      <div className="text-gray-400 text-xs">The estimated amount after service fees</div>
                    </div>
                    <div className="text-gray-900 font-semibold">
                      ${(pricingAmount * 0.9).toFixed(2)}{isHourly ? '/hr' : ''}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Attachments Card */}
            <div className="border border-gray-200 rounded-lg p-5">
              <h4 className="font-semibold text-gray-900 mb-3">Attachments</h4>
              <FileUpload
                onFilesChange={onFileUpload}
                maxFiles={5}
                maxSize={10 * 1024 * 1024}
                acceptedTypes={['image/*', 'video/*', 'application/pdf', '.doc', '.docx', '.txt', '.csv']}
              />
              {proposalData.attachments && proposalData.attachments.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  <div className="flex justify-between items-center">
                    <span className="text-xs font-medium text-gray-500">{proposalData.attachments.length} file(s) selected</span>
                    <button
                      type="button"
                      onClick={() => onProposalInputChange('attachments', [])}
                      className="text-xs text-red-500 hover:text-red-700"
                    >
                      Clear all
                    </button>
                  </div>
                  {proposalData.attachments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2">
                      <span className="text-sm text-gray-700 truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => onRemoveAttachment(index)}
                        className="text-gray-400 hover:text-red-500 ml-2 flex-shrink-0"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Portfolio Projects Card */}
            <div className="border border-gray-200 rounded-lg p-5">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-gray-500" />
                Portfolio projects
              </h4>
              {loadingPortfolio ? (
                <div className="text-center py-4 text-sm text-gray-500">Loading portfolio projects...</div>
              ) : portfolio.length === 0 ? (
                <p className="text-sm text-gray-500">No portfolio projects available.</p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {portfolio.map((project) => {
                    const isSelected = proposalData.portfolioItems?.some(item => item.portfolioId === project._id);
                    return (
                      <div
                        key={project._id}
                        className={`border rounded-lg p-3 cursor-pointer transition-colors ${
                          isSelected ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => onPortfolioSelection(project._id, !isSelected)}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => onPortfolioSelection(project._id, !isSelected)}
                            className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                          />
                          {project.imageUrl && (
                            <img src={project.imageUrl} alt={project.title} className="w-10 h-10 rounded object-cover flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">{project.title}</div>
                            <p className="text-xs text-gray-500 line-clamp-1 mt-0.5">{project.description}</p>
                            {project.technologies && project.technologies.length > 0 && (
                              <div className="flex gap-1 mt-1.5">
                                {project.technologies.slice(0, 3).map((tech, i) => (
                                  <span key={i} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{tech}</span>
                                ))}
                                {project.technologies.length > 3 && (
                                  <span className="text-xs text-gray-400">+{project.technologies.length - 3}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {proposalData.portfolioItems && proposalData.portfolioItems.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <span className="text-xs font-medium text-gray-500 mb-2 block">
                    {proposalData.portfolioItems.length} selected
                  </span>
                  <div className="space-y-1.5">
                    {proposalData.portfolioItems.map((item, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          {item.imageUrl && (
                            <img src={item.imageUrl} alt={item.title} className="w-6 h-6 rounded object-cover flex-shrink-0" />
                          )}
                          <span className="text-sm text-gray-700 truncate">{item.title}</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => onPortfolioSelection(item.portfolioId, false)}
                          className="text-gray-400 hover:text-red-500 ml-2 flex-shrink-0"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Links Card */}
            <div className="border border-gray-200 rounded-lg p-5">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Link2 className="h-4 w-4 text-gray-500" />
                  Relevant links
                </h4>
                <button
                  type="button"
                  onClick={() => {
                    const newLinks = [...(proposalData.links || []), { title: '', url: '' }];
                    onProposalInputChange('links', newLinks);
                  }}
                  className="text-xs font-medium text-primary-600 hover:text-primary-700"
                >
                  + Add link
                </button>
              </div>
              {(!proposalData.links || proposalData.links.length === 0) ? (
                <p className="text-sm text-gray-500">No links added yet.</p>
              ) : (
                <div className="space-y-2">
                  {proposalData.links.map((link, index) => (
                    <div key={index} className="flex gap-2 items-start">
                      <input
                        type="text"
                        placeholder="Title"
                        value={link.title}
                        onChange={(e) => {
                          const newLinks = [...proposalData.links];
                          newLinks[index] = { ...newLinks[index], title: e.target.value };
                          onProposalInputChange('links', newLinks);
                        }}
                        className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <input
                        type="url"
                        placeholder="https://example.com"
                        value={link.url}
                        onChange={(e) => {
                          const newLinks = [...proposalData.links];
                          newLinks[index] = { ...newLinks[index], url: e.target.value };
                          onProposalInputChange('links', newLinks);
                        }}
                        className="flex-[2] px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newLinks = proposalData.links.filter((_, i) => i !== index);
                          onProposalInputChange('links', newLinks);
                        }}
                        className="text-gray-400 hover:text-red-500 p-1.5 flex-shrink-0"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Screening Questions Card */}
            {proposalData.screeningAnswers && proposalData.screeningAnswers.length > 0 && (
              <div className="border border-gray-200 rounded-lg p-5">
                <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <HelpCircle className="h-4 w-4 text-gray-500" />
                  Screening questions
                </h4>
                <div className="space-y-4">
                  {proposalData.screeningAnswers.map((qa, index) => (
                    <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="bg-primary-100 text-primary-800 text-xs font-medium px-2 py-0.5 rounded">
                          Q{index + 1}
                        </span>
                      </div>
                      <p className="text-sm font-medium text-gray-900 mb-2">{qa.question}</p>
                      <textarea
                        value={qa.answer}
                        onChange={(e) => {
                          const newAnswers = [...proposalData.screeningAnswers];
                          newAnswers[index] = { ...newAnswers[index], answer: e.target.value };
                          onProposalInputChange('screeningAnswers', newAnswers);
                        }}
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        placeholder="Your answer..."
                        required
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Submit Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={isCreatingProposal}
                className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isCreatingProposal
                  ? (applyOnUpworkMode ? 'Saving...' : 'Creating...')
                  : (applyOnUpworkMode ? 'Save & Open Upwork' : 'Create Proposal')
                }
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
